import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useGetByIdQuery } from "features/api/orderApi.ts";
import { GridLegacy as Grid } from "@mui/material";
import OrderDetailsLineItemGrid from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemGrid.tsx";
import styles from "../replacements-page-styles.module.css";
import ReplacementsHeader from "components/Replacements/ReplacementsHeader";
import { submittingReplacementOrderActions } from "features/reducers/replacementOrder/submittingReplacementOrder.ts";
import { LineItemsInOrderText, SearchItemText } from "constants/text";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch";
import { useGetAccountsByIdQuery } from "features/api/accountApi.ts";
import { SelectPartsLineItemWrapper } from "components/Replacements/ReplacementHeaderText.styles";
import { configSortCallback, getOrderLineItems, lineItemSortCallback } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { useDispatch } from "react-redux";
import { ReplacementLineGridWrapper } from "pages/Replacements/replacementsPage.styles.ts";
import { UngroupedGridWrapper } from "pages/OrderPages/OrderDetailPage/orderDetailPage.styles.ts";
import { models } from "types/api/viewModels.ts";

interface PartSelectionHeaderProps {
	searchOptions: Array<string>;
	lineItemsCount: number;
	searchValue: string;
	handleSearch: (e: SyntheticEvent, v: string | null) => void;
}

const PartSelectionHeader = ({
	searchOptions = [],
	lineItemsCount = 0,
	searchValue,
	handleSearch
}: PartSelectionHeaderProps) => {
	return (
		<div className={styles.partSelectionHeader}>
			<h4>Select Part(s)</h4>
			<Grid
				container
				className={styles.searchGridContainer}
			>
				<Grid
					item
					xl={4}
					lg={7}
					xs={12}
				>
					<SelectPartsLineItemWrapper data-testid="orderDetails-lineItem-count">
						{lineItemsCount} {LineItemsInOrderText}
					</SelectPartsLineItemWrapper>
				</Grid>
				<Grid
					item
					xl={4}
					lg={5}
					xs={12}
				>
					<SuggestedSearch
						ariaLabel="search-replacements"
						dataTestId="replacements-lineItem-search"
						dropDownOpenOnChar={3}
						placeholder={SearchItemText}
						handleSearch={handleSearch}
						id="replacements-search-line-items"
						searchOptions={searchOptions}
						searchValue={searchValue}
						size="small"
					/>
				</Grid>
			</Grid>
		</div>
	);
};

const Replacements = () => {
	const params = useParams();
	const { data } = useGetByIdQuery(params?.orderId || "");
	const [searchValue, setSearchValue] = useState<string>("");
	const sortedOrderData = useMemo(() => {
		const sortedData: models["OrderDetailViewModel"] | undefined = data ? { ...data } : undefined;
		if (sortedData) {
			const configsCopy = sortedData.configurations
				?.slice()
				?.reduce((accum: models["OrderConfigurationViewModel"][], current) => {
					const matchingLineItem = current.lineItems?.find(
						(item) => `${item?.sku} ${item.description}` === searchValue
					);
					if (matchingLineItem && searchValue) {
						return accum.concat({ ...current, lineItems: [matchingLineItem] });
					} else if (searchValue) {
						return accum;
					}
					return accum.concat(current);
				}, [])
				?.map((config) => ({ ...config, lineItems: config?.lineItems?.slice() }));
			configsCopy?.sort(configSortCallback);
			configsCopy?.forEach((config) => config.lineItems?.sort(lineItemSortCallback));

			return { ...sortedData, configurations: configsCopy };
		}

		return sortedData;
	}, [data, searchValue]);
	const orderLineItems = useMemo(() => getOrderLineItems(data), [data]);
	const { error, data: accounts } = useGetAccountsByIdQuery(params?.orderId ?? "");
	const navigateTo = useNavigate();
	const dispatch = useDispatch();

	useEffect(() => {
		if (error && "status" in error) {
			if (error.status === 403) {
				navigateTo("/unapproved/account");
			}

			if (error.status === 404) {
				navigateTo("/ordernotfound");
			}
		}

		if (accounts?.[0] && (accounts?.[0]?.billTos?.length ?? 0) > 0) {
			const findIsSelectedAddresses = accounts?.[0]?.billTos?.find((address) => address.isSelected);
			if (findIsSelectedAddresses?.isActive) {
				dispatch(submittingReplacementOrderActions.setSelectedBillToAddress(findIsSelectedAddresses));
			}
		}
	}, [error, navigateTo, accounts, dispatch]);

	const handleSearch = (_event: SyntheticEvent, value: string | null) => {
		if (value) {
			setSearchValue(value);
		} else {
			setSearchValue("");
		}
	};

	const searchOptions = useMemo(() => {
		return orderLineItems.map((x) => x?.sku + " " + x?.description);
	}, [orderLineItems]);

	return (
		<div>
			<ReplacementsHeader orderDetail={data} />

			<ReplacementLineGridWrapper
				className={styles.orderListContainer}
				data-testid="replacement-lineitem-page"
			>
				<PartSelectionHeader
					lineItemsCount={orderLineItems?.length || 0}
					searchOptions={searchOptions}
					searchValue={searchValue}
					handleSearch={handleSearch}
				/>
				<UngroupedGridWrapper>
					<OrderDetailsLineItemGrid
						orderDetail={sortedOrderData}
						variant="replacement"
					/>
				</UngroupedGridWrapper>
			</ReplacementLineGridWrapper>
		</div>
	);
};

export default Replacements;
